import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Receipt, CloudArrowUp, CheckCircle, Warning, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";

/**
 * AdminVendorBills — drop LCV/ProCall bills here, AI extracts every field,
 * shows match candidates from your existing WOs. One tap to Link or
 * Create-After-The-Fact WO.
 */
export default function AdminVendorBills() {
  const [bills, setBills] = useState([]);
  const [preview, setPreview] = useState(null);   // { preview, match_candidates }
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const [driveUrl, setDriveUrl] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const importDrive = async () => {
    if (!driveUrl.trim()) return;
    setUploading(true); setPreview(null);
    const tid = toast.loading("Pulling PDFs from Drive folder…", { duration: 600000 });
    try {
      const { data } = await api.post("/vendor-bills/import-drive-folder",
        { folder_url: driveUrl }, { timeout: 600000 });
      toast.dismiss(tid);
      toast.success(
        `Done: ${data.ok}/${data.files_found} · linked ${data.linked} · new WOs ${data.created} · $${(data.total_cents/100).toFixed(2)}${data.fail ? ` · ${data.fail} failed` : ""}`,
        { duration: 10000 });
      setDriveUrl("");
      load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Drive import failed"));
    } finally { setUploading(false); }
  };

  const load = () => {
    api.get("/vendor-bills").then(r => setBills(r.data?.bills || []))
      .catch(() => setBills([]));
  };
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true); setPreview(null);
    // Single file: keep the existing review-then-save flow
    if (files.length === 1) {
      try {
        const fd = new FormData();
        fd.append("file", files[0]);
        const { data } = await api.post("/vendor-bills/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
        });
        setPreview(data);
        toast.success(`Extracted invoice ${data.preview.invoice_number || "?"} · ${data.match_candidates.length} match candidates`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI extract failed"));
      } finally { setUploading(false); }
      return;
    }
    // Multi-file: auto-process each. Link ≥85% match, otherwise create WO.
    const arr = Array.from(files);
    let ok = 0, linked = 0, created = 0, fail = 0, totalCents = 0;
    const tid = toast.loading(`Batch importing ${arr.length} bills…`, { duration: 600000 });
    for (let i = 0; i < arr.length; i++) {
      try {
        const fd = new FormData();
        fd.append("file", arr[i]);
        const { data } = await api.post("/vendor-bills/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
        const cands = data.match_candidates || [];
        const action = cands.length && cands[0].score >= 85 ? "link" : "create_wo";
        const body = { preview: data.preview, action };
        if (action === "link") body.job_id = cands[0].job_id;
        await api.post("/vendor-bills/save", body);
        ok++;
        if (action === "link") linked++; else created++;
        totalCents += data.preview.total_cents || 0;
        toast.loading(`Processed ${i + 1}/${arr.length}…`, { id: tid });
      } catch { fail++; }
    }
    toast.dismiss(tid);
    toast.success(`Done: ${ok}/${arr.length} · linked ${linked} · new WOs ${created} · $${(totalCents/100).toFixed(2)}${fail ? ` · ${fail} failed` : ""}`, { duration: 10000 });
    setUploading(false);
    load();
  };

  const save = async (action, jobId = null) => {
    if (!preview) return;
    setSaving(true);
    try {
      const { data } = await api.post("/vendor-bills/save", {
        preview: preview.preview, action, job_id: jobId,
      });
      toast.success(action === "link" ? `Linked to WO` : `New completed WO created`);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const togglePaid = async (bill) => {
    const next = bill.status === "paid" ? "unpaid" : "paid";
    try { await api.patch(`/vendor-bills/${bill.id}`, { status: next }); load(); }
    catch { toast.error("Couldn't update status"); }
  };

  const [repairing, setRepairing] = useState(false);
  const repairPdfs = async () => {
    if (!window.confirm("Register missing PDF file records and re-sync bill addresses from their matched WOs?")) return;
    setRepairing(true);
    const tid = toast.loading("Repairing bill records…");
    try {
      const { data } = await api.post("/vendor-bills/backfill");
      toast.dismiss(tid);
      toast.success(`Repaired: ${data.files_added} PDFs registered · ${data.addresses_synced} addresses re-synced · ${data.duplicates_merged || 0} duplicates merged`, { duration: 10000 });
      load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Repair failed"));
    } finally { setRepairing(false); }
  };

  const $ = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const unpaidTotal = bills.filter(b => b.status !== "paid").reduce((s, b) => s + (b.total_cents || 0), 0);

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="vendor-bills-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Receipt weight="fill" size={30} className="text-emerald-600"/>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight">Vendor Bills</h1>
          <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-emerald-500 text-slate-900">
            {bills.length} on file
          </span>
          {unpaidTotal > 0 && (
            <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-red-600 text-white">
              {$(unpaidTotal)} unpaid
            </span>
          )}
          <button onClick={repairPdfs} disabled={repairing}
            data-testid="vendor-bills-repair-btn"
            title="Register any bill PDFs that 404 and re-sync addresses from matched WOs. Safe to run repeatedly."
            className="ml-auto h-8 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 disabled:opacity-50">
            {repairing ? "Repairing…" : "Repair PDFs"}
          </button>
        </div>
        <div className="mt-1 text-sm text-slate-600">
          Drop LCV / ProCall / any subcontractor invoice. AI extracts vendor · address · unit · WO # · total. Auto-matches to your existing work orders — creates an after-the-fact completed WO if nothing matches.
        </div>

        {/* Drop zone + Drive folder import */}
        <div className="mt-6 p-6 border-4 border-dashed border-emerald-500 bg-emerald-50 text-center"
          data-testid="vendor-bill-dropzone">
          <input ref={inputRef} type="file" accept=".pdf,image/*" multiple className="hidden"
            onChange={e => { const fs = e.target.files; if (fs?.length) upload(fs); e.target.value = ""; }}
            data-testid="vendor-bill-file-input"/>
          <CloudArrowUp weight="fill" size={40} className="text-emerald-600 mx-auto"/>
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            data-testid="vendor-bill-upload-btn"
            className="mt-3 h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-sm shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-2 disabled:opacity-50">
            <Sparkle weight="fill" size={16}/> {uploading ? "AI reading the bills…" : "Upload Invoices (single or batch)"}
          </button>

          <div className="mt-4 flex items-center gap-2 max-w-lg mx-auto">
            <div className="h-px flex-1 bg-slate-300"/>
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Or paste Drive folder URL</span>
            <div className="h-px flex-1 bg-slate-300"/>
          </div>
          <div className="mt-2 max-w-lg mx-auto flex gap-2">
            <input type="url" placeholder="https://drive.google.com/drive/folders/…"
              value={driveUrl} onChange={e => setDriveUrl(e.target.value)}
              data-testid="vendor-bill-drive-url"
              className="flex-1 h-10 px-3 border-2 border-slate-300 text-sm font-mono"/>
            <button onClick={importDrive} disabled={uploading || !driveUrl.trim()}
              data-testid="vendor-bill-drive-import-btn"
              className="h-10 px-4 bg-slate-900 hover:bg-black text-white font-black uppercase tracking-widest text-[10px] disabled:opacity-50">
              Pull All
            </button>
          </div>
          <div className="mt-2 text-[11px] text-slate-600">
            Folder must be shared as "Anyone with the link · Viewer". Server pulls every PDF, no phone upload needed.
          </div>
        </div>

        {/* Preview + match candidates */}
        {preview && (
          <div className="mt-6 p-4 bg-white border-2 border-emerald-500 shadow-[3px_3px_0_#0f172a]"
            data-testid="vendor-bill-preview">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display font-black text-lg">Extracted from invoice {preview.preview.invoice_number || "?"}</div>
              <span className="text-[10px] font-black uppercase tracking-widest bg-slate-900 text-white px-2 py-0.5">
                Confidence: {preview.preview.confidence}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <Row label="Vendor" value={preview.preview.vendor_name}/>
              <Row label="Address" value={preview.preview.property_address}/>
              <Row label="Unit" value={preview.preview.unit || "—"}/>
              <Row label="Total" value={$(preview.preview.total_cents)} accent/>
              <Row label="Invoice #" value={preview.preview.invoice_number}/>
              <Row label="Invoice date" value={preview.preview.invoice_date}/>
              <Row label="Due date" value={preview.preview.due_date}/>
              <Row label="Vendor WO #" value={preview.preview.vendor_wo_number || "—"}/>
            </div>
            {preview.preview.work_description && (
              <div className="mt-2 p-2 bg-slate-50 border-l-4 border-slate-400 text-xs italic">
                "{preview.preview.work_description}"
              </div>
            )}

            {/* Match candidates */}
            <div className="mt-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                Match to existing WO · {preview.match_candidates.length} candidate{preview.match_candidates.length === 1 ? "" : "s"}
              </div>
              {preview.match_candidates.length === 0 ? (
                <div className="p-3 bg-amber-50 border-2 border-amber-500 flex items-start gap-2">
                  <Warning weight="fill" size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
                  <div className="text-xs">
                    <b>No matching WO found.</b> LCV probably did this work directly — tap <b>Create WO After-the-Fact</b> below to log it as a completed work order.
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  {preview.match_candidates.map(c => (
                    <button key={c.job_id} onClick={() => save("link", c.job_id)} disabled={saving}
                      data-testid={`vendor-bill-link-${c.job_id}`}
                      className="w-full text-left p-2 bg-white border-2 border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-slate-900">
                        {c.score}%
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{c.title || "(untitled)"}</div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {c.wo && `${c.wo} · `}{c.address} {c.unit && `· ${c.unit}`} · {c.status} · {c.created_at}
                        </div>
                      </div>
                      <ArrowSquareOut weight="bold" size={14} className="text-slate-400"/>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 flex gap-2">
              <button onClick={() => setPreview(null)}
                data-testid="vendor-bill-cancel"
                className="h-10 px-3 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[10px]">
                Cancel
              </button>
              <button onClick={() => save("create_wo")} disabled={saving}
                data-testid="vendor-bill-create-wo"
                className="h-10 flex-1 bg-slate-900 hover:bg-black text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center justify-center gap-1">
                <CheckCircle weight="fill" size={12}/> Create WO After-the-Fact
              </button>
            </div>
          </div>
        )}

        {/* Existing bills list */}
        <div className="mt-8">
          <div className="flex items-center gap-2 pb-1 border-b-2 border-slate-900">
            <div className="font-display font-black text-lg uppercase tracking-tight">Bills on file</div>
          </div>
          {bills.length === 0 ? (
            <div className="mt-6 p-8 text-center bg-slate-50 border-2 border-dashed border-slate-300 text-slate-500">
              No vendor bills logged yet.
            </div>
          ) : (
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-300">
                  <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Inv #</th>
                  <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Vendor</th>
                  <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Address / Unit</th>
                  <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Total</th>
                  <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                  <th className="pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {bills.map(b => {
                  const isOpen = expandedId === b.id;
                  return (
                    <>
                      <tr key={b.id} className="border-t border-slate-200 hover:bg-emerald-50 cursor-pointer"
                        onClick={() => setExpandedId(isOpen ? null : b.id)}
                        data-testid={`bill-row-${b.id}`}>
                        <td className="py-2 font-mono text-xs">{b.invoice_number}</td>
                        <td className="py-2 truncate max-w-[160px]">{b.vendor_name}</td>
                        <td className="py-2 text-xs">
                          {b.property_address} {b.unit && `· ${b.unit}`}
                          <div className="text-[10px] text-slate-500 truncate max-w-[280px]">{b.work_description}</div>
                        </td>
                        <td className="py-2 text-right tabular-nums font-black">{$(b.total_cents)}</td>
                        <td className="py-2" onClick={e => e.stopPropagation()}>
                          <button onClick={() => togglePaid(b)}
                            data-testid={`bill-toggle-${b.id}`}
                            className={`h-7 px-2 text-[9px] font-black uppercase tracking-widest ${
                              b.status === "paid" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
                            }`}>
                            {b.status}
                          </button>
                        </td>
                        <td className="py-2 text-right text-slate-400 text-[10px]">
                          {isOpen ? "▲" : "▼"}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50" data-testid={`bill-detail-${b.id}`}>
                          <td colSpan="6" className="p-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="space-y-1 text-xs">
                                <Row label="Invoice date" value={b.invoice_date || "—"}/>
                                <Row label="Due date" value={b.due_date || "—"}/>
                                <Row label="Full description" value={b.work_description || "—"}/>
                              </div>
                              <div className="space-y-1 text-xs">
                                <div>
                                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Linked WO</div>
                                  {b.matched_job_id ? (
                                    <Link to={`/admin/jobs/${b.matched_job_id}`}
                                      data-testid={`bill-open-wo-${b.id}`}
                                      className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-black uppercase text-[10px] tracking-widest mt-1">
                                      Open Work Order <ArrowSquareOut weight="bold" size={11}/>
                                    </Link>
                                  ) : <div className="text-slate-500 italic text-xs">Not linked</div>}
                                </div>
                                <div>
                                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Invoice PDF</div>
                                  {b.pdf_path ? (
                                    <a href={fileUrl(b.pdf_path)} target="_blank" rel="noopener noreferrer"
                                      data-testid={`bill-open-pdf-${b.id}`}
                                      className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-black uppercase text-[10px] tracking-widest mt-1">
                                      View PDF <ArrowSquareOut weight="bold" size={11}/>
                                    </a>
                                  ) : <div className="text-slate-500 italic text-xs">No PDF</div>}
                                </div>
                                <div>
                                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Property</div>
                                  {b.property_address ? (
                                    <Link to={`/admin/properties/${encodeURIComponent(b.property_address)}${b.unit ? `/unit/${encodeURIComponent(b.unit)}` : ""}`}
                                      data-testid={`bill-open-property-${b.id}`}
                                      className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-black uppercase text-[10px] tracking-widest mt-1">
                                      Open {b.unit ? `${b.property_address} · ${b.unit}` : b.property_address} <ArrowSquareOut weight="bold" size={11}/>
                                    </Link>
                                  ) : b.matched_job_id ? (
                                    <Link to={`/admin/jobs/${b.matched_job_id}`}
                                      className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-black uppercase text-[10px] tracking-widest mt-1">
                                      Open Linked WO <ArrowSquareOut weight="bold" size={11}/>
                                    </Link>
                                  ) : <div className="text-slate-500 italic text-xs">No property parsed — link a WO to attach one</div>}
                                </div>
                              </div>
                              <div className="p-2 bg-slate-900 text-white space-y-1 text-xs">
                                <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">TOTAL</div>
                                <div className="font-black text-2xl">{$(b.total_cents)}</div>
                                <div className="text-[10px] opacity-80">Status: {b.status.toUpperCase()}</div>
                                <div className="text-[9px] opacity-60 mt-1">Logged {(b.created_at || "").slice(0, 10)}</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className={`p-2 border ${accent ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono ${accent ? "font-black text-lg" : "text-xs"} truncate`}>{value || "—"}</div>
    </div>
  );
}
